import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { getAuthHeaders } from '../api/auth';
import './UIEngineRenderer.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UIProps = Record<string, any>;

interface UIEngineRendererProps {
  workflowId: string;
  context?: UIProps;
  onComplete?: (sessionId: string) => void;
  onCancel?: (sessionId: string) => void;
  onError?: (error: string) => void;
}

interface UIComponent {
  id?: string;
  type: string;
  props?: UIProps;
  children?: UIComponent[];
  onClick?: { event: string; payload?: UIProps };
  onChange?: { event: string; payload?: UIProps };
  onFocus?: { event: string; payload?: UIProps };
  onBlur?: { event: string; payload?: UIProps };
  onSubmit?: { event: string; payload?: UIProps };
}

interface UIResponse {
  success: boolean;
  sessionId?: string;
  step?: string;
  ui?: {
    type: string;
    layout?: string;
    components: UIComponent[];
  };
  state?: UIProps;
  timers?: Array<{ name: string; startedAt: number; duration?: number; interval?: number }>;
  error?: string;
  message?: string;
}

/**
 * UI Engine Renderer
 * Thin React client that renders database-driven UIs
 * All logic is server-side, client only handles rendering and events
 */
export function UIEngineRenderer({ workflowId, context, onComplete, onCancel, onError }: UIEngineRendererProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [ui, setUI] = useState<UIResponse['ui'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timerIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Initialize session
  useEffect(() => {
    startSession();
  }, [workflowId]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timerIntervals.current.forEach((interval) => clearInterval(interval));
      timerIntervals.current.clear();
    };
  }, []);

  const startSession = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[UIEngine] Starting session', { workflowId, context });

      const response = await fetch(`${API_BASE_URL}/api/ui-engine/session/start`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          workflowId,
          context: context || {},
        }),
      });

      console.log('[UIEngine] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: UIResponse = await response.json();

      console.log('[UIEngine] Session started', { success: data.success, sessionId: data.sessionId });

      if (!data.success || !data.sessionId) {
        throw new Error(data.error || 'Failed to start session');
      }

      setSessionId(data.sessionId);
      setUI(data.ui || null);
      setupTimers(data.timers || []);
      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start session';
      console.error('[UIEngine] Session start failed:', errorMessage);
      setError(errorMessage);
      setLoading(false);
      onError?.(errorMessage);
    }
  };

  const sendEvent = async (eventType: string, payload?: UIProps) => {
    if (!sessionId) {
      console.error('No active session');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/ui-engine/event`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId,
          event: {
            type: eventType,
            payload: payload || {},
          },
          timestamp: new Date().toISOString(),
        }),
      });

      const data: UIResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Event processing failed');
      }

      // Check if workflow completed or cancelled
      if (data.message === 'Workflow completed') {
        onComplete?.(sessionId);
        return;
      }

      if (data.message === 'Workflow cancelled') {
        onCancel?.(sessionId);
        return;
      }

      setUI(data.ui || null);
      setupTimers(data.timers || []);
      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process event';
      setError(errorMessage);
      setLoading(false);
      onError?.(errorMessage);
    }
  };

  const setupTimers = (timers: Array<{ name: string; startedAt: number; duration?: number; interval?: number }>) => {
    // Clear existing timers
    timerIntervals.current.forEach((interval) => clearInterval(interval));
    timerIntervals.current.clear();

    // Set up new timers
    timers.forEach((timer) => {
      if (timer.interval) {
        // Repeating timer
        const intervalId = setInterval(() => {
          sendEvent('timer.tick', { timer: timer.name });
        }, timer.interval);
        timerIntervals.current.set(timer.name, intervalId);
      } else if (timer.duration) {
        // One-shot timer
        const timeoutId = setTimeout(() => {
          sendEvent('timer.complete', { timer: timer.name });
        }, timer.duration) as unknown as NodeJS.Timeout;
        timerIntervals.current.set(timer.name, timeoutId);
      }
    });
  };

  const renderComponent = useCallback((component: UIComponent, index: number): React.JSX.Element | null => {
    const key = component.id || `component-${index}`;

    switch (component.type) {
      case 'heading':
        return React.createElement(
          `h${component.props?.level || 1}`,
          {
            key,
            className: component.props?.class,
            style: component.props?.style,
          },
          component.props?.text
        );

      case 'paragraph':
      case 'text':
        return (
          <p key={key} className={component.props?.class} style={component.props?.style}>
            {component.props?.text}
          </p>
        );

      case 'image':
        return (
          <img
            key={key}
            src={component.props?.src}
            alt={component.props?.alt}
            className={component.props?.class}
            style={component.props?.style}
          />
        );

      case 'button':
        return (
          <button
            key={key}
            type={component.props?.type || 'button'}
            className={component.props?.class}
            style={component.props?.style}
            disabled={component.props?.disabled || loading}
            onClick={() => {
              if (component.onClick) {
                sendEvent(component.onClick.event, component.onClick.payload);
              }
            }}
          >
            {component.props?.text}
            {component.children?.map((child, i) => renderComponent(child, i))}
          </button>
        );

      case 'input':
        return (
          <input
            key={key}
            type={component.props?.type || 'text'}
            className={component.props?.class}
            style={component.props?.style}
            placeholder={component.props?.placeholder}
            value={component.props?.value || ''}
            disabled={component.props?.disabled}
            required={component.props?.required}
            onChange={(e) => {
              if (component.onChange) {
                sendEvent(component.onChange.event, {
                  ...component.onChange.payload,
                  field: component.id,
                  value: e.target.value,
                });
              }
            }}
            onFocus={() => {
              if (component.onFocus) {
                sendEvent(component.onFocus.event, component.onFocus.payload);
              }
            }}
            onBlur={() => {
              if (component.onBlur) {
                sendEvent(component.onBlur.event, component.onBlur.payload);
              }
            }}
          />
        );

      case 'container':
        return (
          <div key={key} className={component.props?.class} style={component.props?.style}>
            {component.children?.map((child, i) => renderComponent(child, i))}
          </div>
        );

      case 'grid':
        return (
          <div
            key={key}
            className={`ui-grid ${component.props?.class || ''}`}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${component.props?.columns || 1}, 1fr)`,
              gap: component.props?.gap || '1rem',
              ...component.props?.style,
            }}
          >
            {component.children?.map((child, i) => renderComponent(child, i))}
          </div>
        );

      case 'stack':
        return (
          <div
            key={key}
            className={`ui-stack ${component.props?.class || ''}`}
            style={{
              display: 'flex',
              flexDirection: component.props?.direction || 'column',
              gap: component.props?.gap || '1rem',
              ...component.props?.style,
            }}
          >
            {component.children?.map((child, i) => renderComponent(child, i))}
          </div>
        );

      case 'form':
        return (
          <form
            key={key}
            className={component.props?.class}
            style={component.props?.style}
            onSubmit={(e) => {
              e.preventDefault();
              if (component.onSubmit) {
                sendEvent(component.onSubmit.event, component.onSubmit.payload);
              }
            }}
          >
            {component.children?.map((child, i) => renderComponent(child, i))}
          </form>
        );

      case 'icon':
        return (
          <span
            key={key}
            className={`ui-icon ${component.props?.class || ''}`}
            style={{
              fontSize: component.props?.size || '1rem',
              color: component.props?.color,
              ...component.props?.style,
            }}
          >
            {component.props?.name || '❓'}
          </span>
        );

      case 'loading-spinner':
        return (
          <div key={key} className={`ui-spinner ${component.props?.class || ''}`}>
            <div className="spinner"></div>
          </div>
        );

      case 'progress-bar':
        return (
          <div key={key} className={`ui-progress ${component.props?.class || ''}`}>
            <div
              className="ui-progress-fill"
              style={{ width: `${component.props?.percent || 0}%` }}
            ></div>
          </div>
        );

      default:
        console.warn(`Unknown component type: ${component.type}`);
        return (
          <div key={key} className="ui-unknown">
            Unknown component: {component.type}
          </div>
        );
    }
  }, [loading, sendEvent]);

  if (loading && !ui) {
    return (
      <div className="ui-engine-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ui-engine-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={startSession}>Retry</button>
      </div>
    );
  }

  if (!ui) {
    return (
      <div className="ui-engine-empty">
        <p>No UI to display</p>
      </div>
    );
  }

  return (
    <div className={`ui-engine-screen ui-layout-${ui.layout || 'default'} ${loading ? 'ui-loading' : ''}`}>
      {ui.components?.map((component, index) => renderComponent(component, index))}
    </div>
  );
}
